import React from 'react';
import { Badge, theme, Tooltip } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useIntl } from '@umijs/max';
import { useWorkItemsStore } from '@/stores/workItemsStore';
import { usePaymentVoucherTodoStore } from '@/stores/paymentVoucherTodoStore';
import { PAYMENT_VOUCHER_PATH } from '@/services/paymentVoucherTodo';

type Props = {
  path?: string;
  pageKey?: string;
  count?: number;
  selected?: boolean;
  children: React.ReactNode;
};

export const WorkBadge: React.FC<Props> = ({
  path,
  pageKey,
  count: explicitCount,
  selected,
  children,
}) => {
  const summary = useWorkItemsStore((state) => state.summary);
  const summaryError = useWorkItemsStore((state) => state.summaryError);
  const voucher = usePaymentVoucherTodoStore((state) => state.todo);
  const voucherError = usePaymentVoucherTodoStore((state) => state.error);
  const isVoucher = path === PAYMENT_VOUCHER_PATH;
  const intl = useIntl();
  const { token } = theme.useToken();
  // Ignore legacy summaries while older backends still include this retired workflow.
  if (pageKey === 'product-no-barcode' || path === '/product-management/no-barcode-products') {
    return <>{children}</>;
  }
  const detail = pageKey
    ? summary?.pages.find((page) => page.pageKey === pageKey)
    : path
      ? summary?.navigation[path]
      : undefined;
  const count =
    explicitCount ??
    (isVoucher
      ? voucher?.count
      : summary?.enabled
        ? pageKey
          ? detail?.badgeCount
          : path
            ? detail?.badgeCount
            : undefined
        : undefined);
  const label =
    detail?.countLabel && detail?.countUnit
      ? `${detail.countLabel}: ${count} ${detail.countUnit}`
      : isVoucher
        ? `待下載: ${count || 0} 張`
        : String(count || '');
  const tooltip = detail?.overdueCount
    ? `${label} · ${intl.formatMessage({ id: 'workItems.overdue', defaultMessage: '逾期' })}: ${detail.overdueCount}`
    : label;
  const showError = isVoucher
    ? voucherError && Boolean(voucher)
    : summaryError && (Boolean(detail) || explicitCount !== undefined);
  if ((!count || count <= 0) && !showError) return <>{children}</>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {children}
      {typeof count === 'number' && count > 0 && (
        <Tooltip title={tooltip}>
          <Badge
            count={count}
            overflowCount={99}
            title={tooltip}
            styles={{
              indicator: {
                height: 18,
                minWidth: 18,
                lineHeight: '18px',
                padding: '0 5px',
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 600,
                boxShadow: 'none',
                background: selected ? token.colorPrimaryBg : token.colorFillSecondary,
                color: selected ? token.colorPrimary : token.colorTextSecondary,
              },
            }}
          />
        </Tooltip>
      )}
      {showError && (
        <Tooltip
          title={intl.formatMessage({
            id: 'workItems.stale',
            defaultMessage: '計數更新失敗，顯示上次結果',
          })}
        >
          <WarningOutlined style={{ color: token.colorWarning, fontSize: 12 }} />
        </Tooltip>
      )}
    </span>
  );
};
